import { useMemo } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';

// Services
import { getImportedDevices } from '../../../../services/inventory.services';

// Utils
import { makeInventoryValidationColumn } from '../../../../utils/columns/makeInventoryValidationColumn';
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';

// Components
import { Table, useStandardTable as useTable } from '../../../common/table';

// Types
import { InventoryContent } from '../../../../constants/Constants';
import { ImportedFileData } from '../../../../types/ImportedFileData';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '70%',
    marginBottom: '10px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '22px',
  },
  heading: {
    margin: '0 0 10px',
  },
  content: {
    paddingLeft: '8px',
    fontSize: '12px',
  },
  tableStyle: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thStyle: {
    backgroundColor: '#f0f0f0',
    padding: '4px',
    textAlign: 'left',
    borderBottom: '1px solid #ddd',
  },

  tdStyle: {
    backgroundColor: '#f9f9f9',
    padding: '4px',
    borderBottom: '1px solid #ddd',
  },
}));

const ValidationAndSummary = ({ importedFileData }: { importedFileData: ImportedFileData }) => {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const classes = useStyles();

  const errors = importedFileData.errors || [];

  const columns = useMemo(() => makeInventoryValidationColumn(), []);

  const fetchImportedDevices = useMemo(() => getImportedDevices(importedFileData.id), [importedFileData.id]);

  const table = useTable<any>({
    columns,
    fetchAsync: fetchImportedDevices,
    serverSide: true,
    shouldFetch: true,
  });

  const hasErrors = errors.length > 0;

  return (
    <>
      <h4 className={classes.heading}>{InventoryContent.detailSummary}</h4>
      <div className={classes.container}>
        <div>
          <div className={classes.contentWrapper}>
            <h5>{InventoryContent.createdDevices}</h5>
            <p className={classes.content}>{importedFileData.createdDevicesCount}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>{InventoryContent.newlyFoundDevices}</h5>
            <p className={classes.content}>{importedFileData.newlyFoundDevicesCount}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>{InventoryContent.totalDevicesFound}</h5>
            <p className={classes.content}>{importedFileData.totalFoundDevicesCount}</p>
          </div>
        </div>
        <div>
          <div className={classes.contentWrapper}>
            <h5>{withTimeMode(InventoryContent.createdDateTime)}</h5>
            <p className={classes.content}>{toStandardDate(importedFileData.createdDateTime)}</p>
          </div>
        </div>
      </div>
      {hasErrors ? (
        <>
          <h4 className={classes.heading}>{InventoryContent.errorsFound}</h4>
          <table className={classes.tableStyle}>
            <thead>
              <tr>
                <th className={classes.thStyle}>{InventoryContent.Id}</th>
                <th className={classes.thStyle}>{InventoryContent.message}</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((item, index) => {
                const [id, message] = item.split(': ');
                return (
                  <tr key={index}>
                    <td className={classes.tdStyle}>{id.replace('#', '')}</td>
                    <td className={classes.tdStyle}>{message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : (
        <>
          <h4 className={classes.heading}>Import Summary</h4>
          <Paper>
            <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={false} getCoreRowModel={getCoreRowModel()} />
          </Paper>
        </>
      )}
    </>
  );
};

export default ValidationAndSummary;
